import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { useCreatePrize, usePrize, useUpdatePrize } from '../../services/gamification';
import { uploadFile, useImageUrls } from '../../services/uploads';
import type { PrizeCategory, PrizeCreate } from '../../types/gamification';
import { ChevronDownIcon, XMarkIcon, CloudArrowUpIcon, GiftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks/useAuth';
import { planSupportsGamification } from '@/config/navigation';
import FeatureUpgradeNotice from '@/components/common/FeatureUpgradeNotice';
import { palette } from '@/styles/figma-tokens';

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS: { value: PrizeCategory; label: string }[] = [
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'gift_card', label: 'Gift Card' },
  { value: 'experience', label: 'Experience' },
  { value: 'digital', label: 'Digital' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'custom', label: 'Custom' },
];

const COLOR_OPTIONS = [
  { label: 'Red', value: 'Red' },
  { label: 'Blue', value: 'Blue' },
  { label: 'Green', value: 'Green' },
  { label: 'Black', value: 'Black' },
  { label: 'White', value: 'White' },
  { label: 'Yellow', value: 'Yellow' },
  { label: 'Purple', value: 'Purple' },
  { label: 'Orange', value: 'Orange' },
  { label: 'Pink', value: 'Pink' },
  { label: 'Grey', value: 'Grey' },
];

const SIZE_OPTIONS = [
  '6', '7', '8', '9', '10', '11', '12',
  'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'One Size',
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  title: string;
  description: string;
  content: string;
  internal_code: string;
  sku: string;
  set_quantity: string;
  category: PrizeCategory;
  colors: string[];
  sizes: string[];
  tags: string[];
  gender: 'men' | 'women' | 'unisex';
  price_points: string;
  is_draft: boolean;
  media_urls: string[];
}

const INITIAL_STATE: FormState = {
  title: '',
  description: '',
  content: '',
  internal_code: '',
  sku: '',
  set_quantity: '',
  category: 'merchandise',
  colors: [],
  sizes: [],
  tags: [],
  gender: 'unisex',
  price_points: '',
  is_draft: true,
  media_urls: [],
};

// ─── Rich text editor ─────────────────────────────────────────────────────────

type ToolbarAction =
  | 'heading' | 'bold' | 'strikethrough' | 'italic'
  | 'ordered-list' | 'unordered-list'
  | 'align-left' | 'align-center' | 'align-right' | 'justify'
  | 'link' | 'code' | 'fullscreen';

function RichTextEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const didInitRef = useRef(false);

  // Seed the contentEditable once with existing content (edit mode). The guard
  // prevents clobbering the cursor/content on subsequent parent re-renders.
  useEffect(() => {
    if (!didInitRef.current && editorRef.current && value) {
      editorRef.current.innerHTML = value;
      didInitRef.current = true;
    }
  }, [value]);

  const exec = (action: ToolbarAction) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    switch (action) {
      case 'bold': document.execCommand('bold'); break;
      case 'italic': document.execCommand('italic'); break;
      case 'strikethrough': document.execCommand('strikeThrough'); break;
      case 'ordered-list': document.execCommand('insertOrderedList'); break;
      case 'unordered-list': document.execCommand('insertUnorderedList'); break;
      case 'align-left': document.execCommand('justifyLeft'); break;
      case 'align-center': document.execCommand('justifyCenter'); break;
      case 'align-right': document.execCommand('justifyRight'); break;
      case 'justify': document.execCommand('justifyFull'); break;
      case 'heading': document.execCommand('formatBlock', false, 'h1'); break;
      case 'link': {
        const url = window.prompt('Enter URL');
        if (url) document.execCommand('createLink', false, url);
        break;
      }
      case 'code': document.execCommand('formatBlock', false, 'pre'); break;
      case 'fullscreen': setIsFullscreen((v) => !v); break;
    }
    onChange(editorRef.current?.innerHTML ?? '');
  };

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  // Figma: toolbar buttons — 28x28, rounded, hover bg
  const tbBtn =
    'relative shrink-0 size-[28px] flex items-center justify-center rounded transition-colors cursor-pointer '
    + 'hover:bg-text-primary/[0.06] text-text-primary';

  return (
    <div
      className={`border border-solid content-stretch flex flex-col h-[240px] items-start overflow-clip relative shrink-0 w-full${
        isFullscreen ? ' fixed inset-4 z-50 shadow-2xl h-auto' : ''
      }`}
      style={{ backgroundColor: 'rgba(189,188,201,0.08)', borderColor: 'rgb(var(--color-border-primary) / 0.5)', borderRadius: '8px' }}
    >
      {/* Toolbar */}
      <div
        className="border-b border-solid content-center flex flex-wrap items-center relative shrink-0 w-full backdrop-blur-sm"
        style={{
          backgroundColor: 'rgb(var(--color-surface-elevated) / 0.8)',
          borderColor: 'rgb(var(--color-border-primary) / 0.5)',
          gap: '8px',
          padding: '10px',
        }}
      >
        {/* Heading 1 dropdown box */}
        <button
          type="button"
          onClick={() => exec('heading')}
          className="border border-solid content-stretch flex h-[32px] items-center relative shrink-0 w-[120px] gap-1"
          style={{
            borderColor: 'rgb(var(--color-border-primary) / 0.5)',
            paddingLeft: '8px',
            paddingRight: '8px',
            borderRadius: '6px',
          }}
          title="Heading 1"
        >
          <span
            className="flex-1 min-h-px min-w-px not-italic relative text-left"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              lineHeight: '22px',
              color: 'rgb(var(--color-text-primary))',
              fontSize: '14px',
              letterSpacing: '0px',
            }}
          >
            Heading 1
          </span>
          <div className="overflow-clip relative shrink-0 size-[16px] flex items-center justify-center">
            <ChevronDownIcon className="w-3 h-3" style={{ color: 'rgb(var(--color-text-primary))' }} />
          </div>
        </button>

        {/* Divider */}
        <div className="h-[16px] shrink-0 w-px" style={{ backgroundColor: 'rgb(var(--color-border-primary) / 0.5)' }} />

        {/* B / S / I */}
        <div className="content-stretch flex items-start relative shrink-0" style={{ gap: '4px' }}>
          <button type="button" onClick={() => exec('bold')} className={tbBtn} title="Bold">
            <strong className="text-[13px]" style={{ color: 'rgb(var(--color-text-primary))' }}>B</strong>
          </button>
          <button type="button" onClick={() => exec('strikethrough')} className={tbBtn} title="Strikethrough">
            <s className="text-[13px]" style={{ color: 'rgb(var(--color-text-secondary))' }}>S</s>
          </button>
          <button type="button" onClick={() => exec('italic')} className={tbBtn} title="Italic">
            <em className="text-[13px]" style={{ color: 'rgb(var(--color-text-primary))' }}>I</em>
          </button>
        </div>

        {/* Divider */}
        <div className="h-[16px] shrink-0 w-px" style={{ backgroundColor: 'rgb(var(--color-border-primary) / 0.5)' }} />

        {/* Lists */}
        <div className="content-stretch flex items-start relative shrink-0" style={{ gap: '4px' }}>
          <button type="button" onClick={() => exec('ordered-list')} className={tbBtn} title="Ordered List">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M2 1h1v3H2V2H1V1h1zm1 9H1V9h2v-.5H1V8h2v2.5H2v.5h1v1zm-1 4.5v-.5h2v2H1v-1h1v-.5H1v-1h1zM5 3h9V2H5v1zm0 6h9V8H5v1zm0 6h9v-1H5v1z" />
            </svg>
          </button>
          <button type="button" onClick={() => exec('unordered-list')} className={tbBtn} title="Unordered List">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M2 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm3-1h9V2H5v1zm0 6h9V8H5v1zm0 6h9v-1H5v1zM2 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="h-[16px] shrink-0 w-px" style={{ backgroundColor: 'rgb(var(--color-border-primary) / 0.5)' }} />

        {/* Alignment */}
        <div className="content-stretch flex items-start relative shrink-0" style={{ gap: '4px' }}>
          <button type="button" onClick={() => exec('align-left')} className={tbBtn} title="Align Left">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M2 2h12v1H2V2zm0 3h8v1H2V5zm0 3h12v1H2V8zm0 3h8v1H2v-1z" />
            </svg>
          </button>
          <button type="button" onClick={() => exec('align-center')} className={tbBtn} title="Align Center">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M2 2h12v1H2V2zm2 3h8v1H4V5zm-2 3h12v1H2V8zm2 3h8v1H4v-1z" />
            </svg>
          </button>
          <button type="button" onClick={() => exec('align-right')} className={tbBtn} title="Align Right">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M2 2h12v1H2V2zm4 3h8v1H6V5zm-4 3h12v1H2V8zm4 3h8v1H6v-1z" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="h-[16px] shrink-0 w-px" style={{ backgroundColor: 'rgb(var(--color-border-primary) / 0.5)' }} />

        {/* Link / Attachment / Image */}
        <div className="content-stretch flex items-start relative shrink-0" style={{ gap: '4px' }}>
          <button type="button" onClick={() => exec('link')} className={tbBtn} title="Insert Link">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M6.354 5.5H4a3 3 0 0 0 0 6h3a3 3 0 0 0 2.83-4H9c-.086 0-.17.01-.25.031A2 2 0 0 1 7 10H4a2 2 0 1 1 0-4h1.535c.218-.376.495-.714.82-1z" />
              <path d="M9 5.5a3 3 0 0 0-2.83 4h1.098A2 2 0 0 1 9 6.5h3a2 2 0 1 1 0 4h-1.535a4.02 4.02 0 0 1-.82 1H12a3 3 0 1 0 0-6H9z" />
            </svg>
          </button>
          <button type="button" className={tbBtn} title="Attachment">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0V3z" />
            </svg>
          </button>
          <button type="button" className={tbBtn} title="Embed Image">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
              <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="h-[16px] shrink-0 w-px" style={{ backgroundColor: 'rgb(var(--color-border-primary) / 0.5)' }} />

        {/* Table / Code / Fullscreen */}
        <div className="content-stretch flex items-start relative shrink-0" style={{ gap: '4px' }}>
          <button type="button" className={tbBtn} title="Insert Table">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z" />
            </svg>
          </button>
          <button type="button" onClick={() => exec('code')} className={tbBtn} title="Code Block">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M5.854 4.854a.5.5 0 1 0-.708-.708l-3.5 3.5a.5.5 0 0 0 0 .708l3.5 3.5a.5.5 0 0 0 .708-.708L2.707 8l3.147-3.146zm4.292 0a.5.5 0 0 1 .708-.708l3.5 3.5a.5.5 0 0 1 0 .708l-3.5 3.5a.5.5 0 0 1-.708-.708L13.293 8l-3.147-3.146z" />
            </svg>
          </button>
          <button type="button" onClick={() => exec('fullscreen')} className={tbBtn} title="Fullscreen">
            <svg viewBox="0 0 16 16" className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
              <path d="M1.5 1h4a.5.5 0 0 1 0 1H2v3.5a.5.5 0 0 1-1 0V1.5A.5.5 0 0 1 1.5 1zm14 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V2h-3.5a.5.5 0 0 1 0-1h4zm-14 14a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 1 0V14h3.5a.5.5 0 0 1 0 1h-4zm14 0h-4a.5.5 0 0 1 0-1H14v-3.5a.5.5 0 0 1 1 0v4a.5.5 0 0 1-.5.5z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Editable area */}
      <div
        className="content-stretch flex flex-[1_0_0] items-start min-h-px min-w-px relative w-full overflow-y-auto"
        style={{ paddingLeft: '16px', paddingRight: '16px', paddingTop: '12px', paddingBottom: '12px' }}
      >
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          className="flex-1 min-h-px min-w-px not-italic relative focus:outline-none [&:empty]:before:content-['Write_something_awesome...']"
          style={{
            fontFamily: "'Poppins', sans-serif",
            lineHeight: '22px',
            color: 'rgb(var(--color-text-primary))',
            fontSize: '14px',
            letterSpacing: '0px',
          }}
        />
      </div>
    </div>
  );
}

// ─── Multi-select dropdown ────────────────────────────────────────────────────

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  floatingLabel,
}: {
  label: string;
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  floatingLabel?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const displayValue =
    selected.length === 0
      ? ''
      : selected.length <= 3
      ? selected.join(', ')
      : `${selected.slice(0, 3).join(', ')} +${selected.length - 3}`;

  const hasValue = selected.length > 0;

  return (
    <div className="content-stretch flex flex-[1_0_0] flex-col h-[54px] items-start min-h-px min-w-[280px] relative">
      <div
        className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
        style={{
          borderColor: 'rgb(var(--color-border-primary) / 0.5)',
          paddingLeft: '14px',
          paddingRight: '14px',
          borderRadius: '8px',
        }}
      >
        {/* Floating label when has value */}
        {floatingLabel && hasValue && (
          <div className="absolute content-stretch flex items-center left-[13px] top-[-9px]" style={{ paddingLeft: '2px', paddingRight: '2px' }}>
            <div className="-translate-y-1/2 absolute h-[2px] left-0 right-0 top-1/2" style={{ backgroundColor: 'rgb(var(--color-surface-primary))' }} />
            <div
              className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 whitespace-nowrap"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                color: 'rgb(var(--color-text-secondary))',
                fontSize: '12px',
              }}
            >
              <p style={{ lineHeight: '18px' }}>{label}</p>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative w-full text-left"
          style={{ paddingTop: '16px', paddingBottom: '16px' }}
        >
          <div
            className="flex flex-[1_0_0] flex-col justify-center leading-[0] min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '15px',
              color: hasValue ? 'rgb(var(--color-text-primary))' : 'rgb(var(--color-text-tertiary))',
            }}
          >
            <p className="leading-[24px] overflow-hidden text-[15px] text-ellipsis">
              {hasValue ? displayValue : label}
            </p>
          </div>
        </button>
        <div className="-translate-y-1/2 absolute content-stretch flex items-center right-[-1px] top-1/2 pointer-events-none" style={{ paddingRight: '10px' }}>
          <div className="overflow-clip relative shrink-0 size-[18px] flex items-center justify-center">
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'rgb(var(--color-text-secondary))' }} />
          </div>
        </div>
      </div>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-full mt-1 z-20 w-full rounded-xl border shadow-lg py-1 max-h-48 overflow-y-auto"
            style={{ borderColor: 'rgb(var(--color-border-primary) / 0.5)', backgroundColor: 'rgb(var(--color-surface-elevated))' }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="w-full flex items-center gap-2 px-3 py-2 text-[15px] transition-colors hover:bg-[rgba(189,188,201,0.08)]"
                style={{ color: 'rgb(var(--color-text-primary))' }}
              >
                <span
                  className="w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center"
                  style={{
                    backgroundColor: selected.includes(opt.value) ? 'rgb(var(--color-primary))' : 'transparent',
                    borderColor: selected.includes(opt.value) ? 'rgb(var(--color-primary))' : palette.grey['600'],
                  }}
                >
                  {selected.includes(opt.value) && (
                    <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Upload illustration (matches Figma IllustrationsIllustrationUpload) ──────

function UploadIllustration() {
  return (
    <div className="overflow-clip relative shrink-0 size-[120px]">
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none">
        <rect x="18" y="30" width="70" height="55" rx="6" fill="#f0eafa" stroke="#c4aef0" strokeWidth="1.5" transform="rotate(-6 18 30)" />
        <rect x="25" y="28" width="70" height="55" rx="6" fill="rgb(var(--color-primary-50))" stroke="rgb(var(--color-primary-200))" strokeWidth="1.5" transform="rotate(-2 25 28)" />
        <rect x="24" y="32" width="72" height="56" rx="6" fill="#f7f4fd" stroke="#ddd8f0" strokeWidth="1.5" />
        <circle cx="52" cy="50" r="7" fill="#e8ddf7" />
        <path d="M24 68 L40 52 L54 62 L64 54 L96 72 L96 88 L24 88 Z" fill="#e0d4f5" />
        <circle cx="88" cy="38" r="14" fill="rgb(var(--color-primary))" />
        <path d="M88 33 L88 43 M84 37 L88 33 L92 37" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

// ─── Collapsible card (Figma card pattern) ────────────────────────────────────

function CollapsibleCard({
  title,
  subtitle,
  children,
  defaultOpen = true,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="content-stretch flex flex-col items-end relative w-full"
      style={{
        borderRadius: '16px',
        // Border-only, no halo, and the same on every card — one card used to
        // carry an accent hairline while its neighbours had none, and the
        // drop shadows glowed in dark mode (Aleix, 2026-09-10; the same
        // treatment the Organization dialogs got in August).
        border: '1px solid rgb(var(--color-border-primary) / 0.5)',
      }}
    >
      {/* Background */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ borderRadius: '16px' }}>
        <div className="absolute inset-0" style={{ backgroundColor: 'rgb(var(--color-surface-primary))', borderRadius: '16px' }} />
        <div
          className="absolute inset-0 opacity-60"
          style={{
            borderRadius: '16px',
            background:
              'radial-gradient(ellipse 80% 50% at 90% -10%, rgb(var(--color-primary) / 0.07) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 10% 100%, rgb(var(--color-secondary) / 0.05) 0%, transparent 60%)',
          }}
        />
      </div>

      {/* CardHeader */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="border-b border-solid content-stretch flex items-start relative shrink-0 w-full text-left hover:bg-black/[0.02] transition-colors"
        style={{
          borderColor: 'rgb(var(--color-border-primary) / 0.5)',
          gap: '16px',
          paddingLeft: '24px',
          paddingRight: '16px',
          paddingTop: '24px',
          paddingBottom: '24px',
        }}
      >
        <div className="content-stretch flex flex-[1_0_0] items-center min-h-px min-w-px relative" style={{ gap: '16px' }}>
          <div className="content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px not-italic overflow-clip relative" style={{ gap: '4px' }}>
            <p
              className="relative shrink-0 w-full"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                lineHeight: '28px',
                color: 'rgb(var(--color-text-primary))',
                fontSize: '18px',
                letterSpacing: '0px',
              }}
            >
              {title}
            </p>
            <p
              className="h-[22px] relative shrink-0 w-full"
              style={{
                fontFamily: "'Poppins', sans-serif",
                lineHeight: '22px',
                color: 'rgb(var(--color-text-secondary))',
                fontSize: '14px',
                letterSpacing: '0px',
              }}
            >
              {subtitle}
            </p>
          </div>
        </div>
        <div
          className="content-stretch flex items-center justify-center relative shrink-0"
          style={{ padding: '8px', borderRadius: '500px' }}
        >
          <div className="overflow-clip relative shrink-0 size-[20px] flex items-center justify-center">
            <ChevronDownIcon
              className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              style={{ color: 'rgb(var(--color-text-secondary))' }}
            />
          </div>
        </div>
      </button>

      {/* Card content */}
      {open && (
        <div
          className="content-stretch flex flex-col items-start relative shrink-0 w-full"
          style={{ gap: '24px', padding: '24px' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Outlined text field (Figma style) ───────────────────────────────────────

function OutlinedInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  min,
  maxLength,
  hasError,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  min?: number;
  maxLength?: number;
  hasError?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`border border-solid content-stretch flex items-center relative shrink-0 w-full ${className ?? ''}`}
      style={{
        borderColor: hasError ? palette.error['500'] : 'rgb(var(--color-border-primary) / 0.5)',
        paddingLeft: '14px',
        paddingRight: '14px',
        borderRadius: '8px',
      }}
    >
      <div
        className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative"
        style={{ paddingTop: '16px', paddingBottom: '16px' }}
      >
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          min={min}
          maxLength={maxLength}
          className="flex flex-[1_0_0] flex-col justify-center leading-[0] min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap bg-transparent focus:outline-none w-full"
          style={{
            fontFamily: "'Poppins', sans-serif",
            color: 'rgb(var(--color-text-primary))',
            fontSize: '15px',
          }}
        />
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PrizeCreatePage() {
  const navigate = useNavigate();
  const { activeMembership } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const createMutation = useCreatePrize();
  const updateMutation = useUpdatePrize();
  const { data: existingPrize } = usePrize(id);

  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  // Existing S3 media keys (edit mode), kept separate from the newly-added
  // blob previews in `form.media_urls`; resolved to presigned URLs for preview.
  const [existingKeys, setExistingKeys] = useState<string[]>([]);
  const existingPreviewUrls = useImageUrls(existingKeys);
  const initializedRef = useRef(false);

  // Prefill the form once the prize being edited has loaded.
  useEffect(() => {
    if (isEdit && existingPrize && !initializedRef.current) {
      initializedRef.current = true;
      setForm({
        title: existingPrize.title,
        description: existingPrize.description ?? '',
        content: existingPrize.content ?? '',
        internal_code: existingPrize.internal_code ?? '',
        sku: existingPrize.sku ?? '',
        set_quantity: existingPrize.set_quantity ? String(existingPrize.set_quantity) : '',
        category: existingPrize.category,
        colors: existingPrize.colors ?? [],
        sizes: existingPrize.sizes ?? [],
        tags: existingPrize.tags ?? [],
        gender: existingPrize.gender,
        price_points: String(existingPrize.price_points ?? ''),
        is_draft: existingPrize.is_draft,
        media_urls: [],
      });
      setExistingKeys(existingPrize.media_urls ?? []);
    }
  }, [isEdit, existingPrize]);

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track actual File objects for upload — indices match form.media_urls (preview blob URLs)
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const updateField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // ─── Validation ───────────────────────────────────────────────────
  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (form.price_points === '' || parseInt(form.price_points, 10) < 0)
      errs.price_points = 'Points must be 0 or more';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return false;
    }
    return true;
  };

  // ─── Build payload ────────────────────────────────────────────────
  const buildPayload = (isDraft: boolean): PrizeCreate => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    content: form.content.trim() || null,
    category: form.category,
    price_points: parseInt(form.price_points, 10) || 0,
    retail_amount: null,
    retail_currency: 'USD',
    set_quantity: form.set_quantity ? parseInt(form.set_quantity, 10) : 0,
    gender: form.gender,
    sku: form.sku.trim() || null,
    internal_code: form.internal_code.trim() || null,
    is_draft: isDraft,
    colors: form.colors,
    sizes: form.sizes,
    tags: form.tags,
    media_urls: form.media_urls,
  });

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    const isDraft = form.is_draft;
    try {
      // Upload newly-added image files to S3 and collect their keys
      let newKeys: string[] = [];
      if (pendingFiles.length > 0) {
        const tempResourceId = crypto.randomUUID();
        const uploadResults = await Promise.all(
          pendingFiles.map((file) =>
            uploadFile({
              file,
              resource_type: 'prize_media',
              resource_id: tempResourceId,
            })
          )
        );
        newKeys = uploadResults.map((r) => r.key);
      }

      const payload = buildPayload(isDraft);
      // In edit mode, retained existing keys come first, then any new uploads.
      payload.media_urls = isEdit ? [...existingKeys, ...newKeys] : newKeys;
      if (isEdit && id) {
        await updateMutation.mutateAsync({ prizeId: id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      // Revoke blob URLs
      form.media_urls.forEach((url) => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
      toast.success(isEdit ? 'Prize updated' : isDraft ? 'Prize saved as draft' : 'Prize published');
      navigate(isEdit && id ? `/prizes/${id}` : '/prizes');
    } catch {
      toast.error(isEdit ? 'Failed to update prize' : 'Failed to create prize');
    }
  };

  // ─── Tag helpers ──────────────────────────────────────────────────
  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || form.tags.includes(tag) || form.tags.length >= 20) return;
    updateField('tags', [...form.tags, tag]);
  };

  const removeTag = (tag: string) => {
    updateField('tags', form.tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
      setTagInput('');
    }
    if (e.key === 'Backspace' && !tagInput && form.tags.length > 0) {
      updateField('tags', form.tags.slice(0, -1));
    }
  };

  // ─── Image helpers ────────────────────────────────────────────────
  const addFiles = (files: File[]) => {
    const remaining = 10 - (existingKeys.length + form.media_urls.length);
    const toAdd = files.slice(0, remaining);
    const newUrls = toAdd.map((f) => URL.createObjectURL(f));
    updateField('media_urls', [...form.media_urls, ...newUrls]);
    setPendingFiles((prev) => [...prev, ...toAdd]);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    addFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith('image/'));
    addFiles(files);
    e.target.value = '';
  };

  const removeImage = (idx: number) => {
    const url = form.media_urls[idx];
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    updateField('media_urls', form.media_urls.filter((_, i) => i !== idx));
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExistingImage = (idx: number) => {
    setExistingKeys((prev) => prev.filter((_, i) => i !== idx));
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Prize management is a Pro/Enterprise feature. Block the create/edit form on
  // lower tiers even when reached directly by URL — the backend rejects the
  // write regardless, this keeps the UI honest.
  const hasGamification = planSupportsGamification(activeMembership?.tenant?.subscription_plan);

  // ─── Render ───────────────────────────────────────────────────────
  if (!hasGamification) {
    return (
      <FeatureUpgradeNotice
        feature="Prize Management"
        description="Reward learners with prizes they can redeem using their points."
        currentPlan={activeMembership?.tenant?.subscription_plan}
        icon={<GiftIcon className="w-8 h-8" />}
      />
    );
  }

  return (
    <motion.div
      {...slideUp}
      className="content-stretch flex flex-col items-start relative w-full pb-[120px]"
      style={{ gap: '40px', paddingLeft: '40px', paddingRight: '40px', paddingTop: '80px' }}
    >
      {/* ── Breadcrumbs + Page title ─────────────────────────────── */}
      <div className="content-start flex flex-wrap items-start justify-end relative shrink-0 w-full" style={{ gap: '16px' }}>
        <div className="content-stretch flex flex-[1_0_0] flex-col items-start justify-center min-h-px min-w-[280px] relative" style={{ gap: '8px' }}>
          {/* Title — h4 Bold */}
          <p
            className="not-italic relative shrink-0 whitespace-nowrap"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              lineHeight: '36px',
              color: 'rgb(var(--color-text-primary))',
              fontSize: '24px',
              letterSpacing: '0px',
            }}
          >
            {isEdit ? 'Edit Prize' : 'Create a new Prize'}
          </p>
          {/* Breadcrumb links — PageHeader path style */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
            <Link
              to="/dashboard"
              className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
            >
              Dashboard
            </Link>
            <span className="body-small text-text-secondary">•</span>
            <Link
              to="/prizes"
              className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
            >
              Prize Management
            </Link>
            {isEdit && (
              <>
                <span className="body-small text-text-secondary">•</span>
                {existingPrize ? (
                  <Link
                    to={`/prizes/${id}`}
                    className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors truncate max-w-[240px]"
                  >
                    {existingPrize.title}
                  </Link>
                ) : (
                  <span className="body-small text-text-secondary">Prize</span>
                )}
              </>
            )}
            <span className="body-small text-text-secondary">•</span>
            <span className="body-small text-primary-600 dark:text-primary-350 font-medium">
              {isEdit ? 'Edit' : 'Create'}
            </span>
          </nav>
        </div>
      </div>

      {/* ── Cards stack ─────────────────────────────────────────────── */}
      <div className="content-stretch flex flex-col items-center relative shrink-0 w-full" style={{ gap: '40px' }}>

        {/* ── Details card ─────────────────────────────────────────── */}
        <CollapsibleCard title="Details" subtitle="Title, short description, image...">

          {/* Product Name */}
          <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
            <OutlinedInput
              value={form.title}
              onChange={(v) => updateField('title', v)}
              placeholder="Product Name"
              maxLength={255}
              hasError={!!errors.title}
            />
            {errors.title && (
              <p className="mt-1 text-xs text-error-500 px-3">{errors.title}</p>
            )}
          </div>

          {/* Sub Description textarea */}
          <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
            <div
              className="border border-solid content-stretch flex items-start min-h-[98px] overflow-clip relative shrink-0 w-full"
              style={{
                borderColor: 'rgb(var(--color-border-primary) / 0.5)',
                paddingLeft: '14px',
                paddingRight: '14px',
                paddingTop: '16px',
                paddingBottom: '16px',
                borderRadius: '8px',
              }}
            >
              <textarea
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Sub Description"
                rows={4}
                maxLength={500}
                className="flex-[1_0_0] min-h-px min-w-px not-italic relative whitespace-pre-wrap bg-transparent focus:outline-none resize-none w-full"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  color: 'rgb(var(--color-text-primary))',
                  fontSize: '15px',
                }}
              />
            </div>
          </div>

          {/* Content — rich text editor */}
          <div className="content-stretch flex flex-col items-start relative shrink-0 w-full" style={{ gap: '12px' }}>
            <p
              className="not-italic relative shrink-0 w-full"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                lineHeight: '22px',
                color: 'rgb(var(--color-text-primary))',
                fontSize: '14px',
                letterSpacing: '0px',
              }}
            >
              Content
            </p>
            <RichTextEditor value={form.content} onChange={(v) => updateField('content', v)} />
          </div>

          {/* Images */}
          <div className="content-stretch flex flex-col items-start relative shrink-0 w-full" style={{ gap: '12px' }}>
            <p
              className="not-italic relative shrink-0 w-full"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                lineHeight: '22px',
                color: 'rgb(var(--color-text-primary))',
                fontSize: '14px',
                letterSpacing: '0px',
              }}
            >
              Images
            </p>

            <div className="content-stretch flex flex-col items-center justify-center relative shrink-0 w-full">
              {/* Drop zone */}
              <div
                onDrop={handleFileDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className="border border-dashed content-stretch flex flex-col items-center justify-center overflow-clip relative shrink-0 w-full cursor-pointer transition-colors hover:bg-[rgb(var(--color-primary) / 0.04)]"
                style={{
                  backgroundColor: 'rgba(189,188,201,0.08)',
                  borderColor: 'rgb(var(--color-border-primary) / 0.5)',
                  padding: '40px',
                  borderRadius: '16px',
                }}
              >
                <UploadIllustration />
                <div className="content-stretch flex flex-col items-center not-italic relative shrink-0 text-center w-full" style={{ gap: '8px' }}>
                  <p
                    className="relative shrink-0 w-full"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      lineHeight: '28px',
                      color: 'rgb(var(--color-text-primary))',
                      fontSize: '18px',
                      letterSpacing: '0px',
                    }}
                  >
                    Drop or Select File
                  </p>
                  <p
                    className="relative shrink-0 leading-[0] w-full"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      color: 'rgb(var(--color-text-secondary))',
                      fontSize: '14px',
                      letterSpacing: '0px',
                    }}
                  >
                    <span style={{ lineHeight: '22px' }}>Drop files here or click to </span>
                    <span style={{ lineHeight: '22px', color: 'rgb(var(--color-primary))' }}>browse</span>
                    <span style={{ lineHeight: '22px' }}> through your machine.</span>
                  </p>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Preview thumbnails + action buttons */}
              <div className="content-stretch flex flex-col items-start relative shrink-0 w-full" style={{ borderRadius: '8px' }}>
                {existingKeys.length > 0 && (
                  <div
                    className="content-start flex flex-wrap items-start relative shrink-0 w-full"
                    style={{ gap: '8px', paddingTop: '20px' }}
                  >
                    {existingPreviewUrls.map((url, idx) => (
                      <div
                        key={`existing-${idx}`}
                        className="content-stretch flex flex-col items-end relative shrink-0 size-[72px]"
                        style={{ borderRadius: '12px' }}
                      >
                        <div className="flex-[1_0_0] min-h-px min-w-px relative w-full overflow-hidden" style={{ borderRadius: '12px' }}>
                          <img
                            src={url}
                            alt=""
                            className="absolute inset-0 max-w-none object-cover pointer-events-none size-full"
                            style={{ borderRadius: '12px' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExistingImage(idx)}
                          className="absolute content-stretch flex items-center justify-center left-[40px] top-[4px] hover:bg-[rgba(38,37,45,0.72)] transition-colors"
                          style={{
                            backgroundColor: 'rgba(38,37,45,0.48)',
                            padding: '5px',
                            borderRadius: '500px',
                          }}
                          aria-label="Remove image"
                        >
                          <XMarkIcon className="w-[18px] h-[18px] text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {form.media_urls.length > 0 && (
                  <div
                    className="content-start flex flex-wrap items-start relative shrink-0 w-full"
                    style={{ gap: '8px', paddingTop: '20px' }}
                  >
                    {form.media_urls.map((url, idx) => (
                      <div
                        key={idx}
                        className="content-stretch flex flex-col items-end relative shrink-0 size-[72px]"
                        style={{ borderRadius: '12px' }}
                      >
                        <div className="flex-[1_0_0] min-h-px min-w-px relative w-full overflow-hidden" style={{ borderRadius: '12px' }}>
                          <img
                            src={url}
                            alt=""
                            className="absolute inset-0 max-w-none object-cover pointer-events-none size-full"
                            style={{ borderRadius: '12px' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute content-stretch flex items-center justify-center left-[40px] top-[4px] hover:bg-[rgba(38,37,45,0.72)] transition-colors"
                          style={{
                            backgroundColor: 'rgba(38,37,45,0.48)',
                            padding: '5px',
                            borderRadius: '500px',
                          }}
                          aria-label="Remove image"
                        >
                          <XMarkIcon className="w-[18px] h-[18px] text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(existingKeys.length > 0 || form.media_urls.length > 0) && (
                  <div
                    className="content-stretch flex items-start justify-end relative shrink-0 w-full"
                    style={{ gap: '12px', paddingTop: '20px' }}
                  >
                    {/* Remove all */}
                    <button
                      type="button"
                      onClick={() => { form.media_urls.forEach((url) => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); }); updateField('media_urls', []); setPendingFiles([]); setExistingKeys([]); }}
                      className="border border-solid content-stretch flex items-center justify-center min-h-[36px] min-w-[64px] relative shrink-0 hover:bg-[rgba(189,188,201,0.08)] transition-colors"
                      style={{
                        borderColor: 'rgb(var(--color-border-primary) / 0.5)',
                        gap: '8px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        borderRadius: '8px',
                      }}
                    >
                      <div
                        className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-center whitespace-nowrap"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 700,
                          color: 'rgb(var(--color-text-primary))',
                          fontSize: '14px',
                        }}
                      >
                        <p style={{ lineHeight: '24px' }}>Remove all</p>
                      </div>
                    </button>
                    {/* Upload */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-primary content-stretch flex items-center justify-center min-h-[36px] min-w-[64px] relative shrink-0 hover:opacity-90 transition-opacity"
                      style={{
                        gap: '8px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        borderRadius: '8px',
                      }}
                    >
                      <CloudArrowUpIcon className="w-5 h-5 text-white" />
                      <div
                        className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-center whitespace-nowrap"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 700,
                          color: 'white',
                          fontSize: '14px',
                        }}
                      >
                        <p style={{ lineHeight: '24px' }}>Upload</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CollapsibleCard>

        {/* ── Properties card ───────────────────────────────────────── */}
        <CollapsibleCard
          title="Properties"
          subtitle="Additional functions and attributes..."
        >
          {/* Row 1: Product Code, Product SKU, Quantity */}
          <div className="content-start flex flex-wrap items-start relative shrink-0 w-full" style={{ gap: '24px 16px' }}>
            {/* Product Code */}
            <div className="content-stretch flex flex-[1_0_0] flex-col h-[54px] items-start min-h-px min-w-[280px] relative">
              <div
                className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
                style={{ borderColor: 'rgb(var(--color-border-primary) / 0.5)', paddingLeft: '14px', paddingRight: '14px', borderRadius: '8px' }}
              >
                <div
                  className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative"
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                >
                  <input
                    type="text"
                    value={form.internal_code}
                    onChange={(e) => updateField('internal_code', e.target.value)}
                    placeholder="Product Code"
                    maxLength={100}
                    className="flex flex-[1_0_0] flex-col justify-center leading-[0] min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap bg-transparent focus:outline-none w-full"
                    style={{ fontFamily: "'Poppins', sans-serif", color: 'rgb(var(--color-text-primary))', fontSize: '15px' }}
                  />
                </div>
              </div>
            </div>

            {/* Product SKU */}
            <div className="content-stretch flex flex-[1_0_0] flex-col h-[54px] items-start min-h-px min-w-[280px] relative">
              <div
                className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
                style={{ borderColor: 'rgb(var(--color-border-primary) / 0.5)', paddingLeft: '14px', paddingRight: '14px', borderRadius: '8px' }}
              >
                <div
                  className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative"
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                >
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => updateField('sku', e.target.value)}
                    placeholder="Product SKU"
                    maxLength={100}
                    className="flex flex-[1_0_0] flex-col justify-center leading-[0] min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap bg-transparent focus:outline-none w-full"
                    style={{ fontFamily: "'Poppins', sans-serif", color: 'rgb(var(--color-text-primary))', fontSize: '15px' }}
                  />
                </div>
              </div>
            </div>

            {/* Quantity */}
            <div className="content-stretch flex flex-[1_0_0] flex-col h-[80px] items-start min-h-px min-w-[280px] relative">
              <div
                className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
                style={{ borderColor: 'rgb(var(--color-border-primary) / 0.5)', paddingLeft: '14px', paddingRight: '14px', borderRadius: '8px' }}
              >
                <div
                  className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative"
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                >
                  <input
                    type="number"
                    value={form.set_quantity}
                    onChange={(e) => updateField('set_quantity', e.target.value)}
                    placeholder="Quantity"
                    min={0}
                    className="flex flex-[1_0_0] flex-col justify-center leading-[0] min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap bg-transparent focus:outline-none w-full"
                    style={{ fontFamily: "'Poppins', sans-serif", color: 'rgb(var(--color-text-primary))', fontSize: '15px' }}
                  />
                </div>
              </div>
              {/* Helper text */}
              <div
                className="content-stretch flex items-center relative shrink-0 w-full"
                style={{ gap: '4px', paddingTop: '6px', paddingLeft: '12px', paddingRight: '12px' }}
              >
                <svg viewBox="0 0 16 16" className="relative shrink-0 size-[16px]" style={{ color: 'rgb(var(--color-text-secondary))' }} fill="currentColor">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                </svg>
                <p
                  className="flex-[1_0_0] min-h-px min-w-px not-italic relative"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    lineHeight: '18px',
                    color: 'rgb(var(--color-text-secondary))',
                    fontSize: '12px',
                    letterSpacing: '0px',
                  }}
                >
                  Set 0 if out of stock
                </p>
              </div>
            </div>
          </div>

          {/* Row 2: Category, Colors, Sizes */}
          <div className="content-start flex flex-wrap items-start relative shrink-0 w-full" style={{ gap: '24px 16px' }}>
            {/* Category select */}
            <div className="content-stretch flex flex-[1_0_0] flex-col min-h-[54px] items-start min-w-[280px] relative">
              <div
                className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
                style={{ borderColor: 'rgb(var(--color-border-primary) / 0.5)', paddingLeft: '14px', paddingRight: '14px', borderRadius: '8px' }}
              >
                <div
                  className="content-stretch flex flex-[1_0_0] items-center min-h-px min-w-px relative"
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                >
                  <select
                    value={form.category}
                    onChange={(e) => updateField('category', e.target.value as PrizeCategory)}
                    className="flex flex-[1_0_0] flex-col justify-center min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap bg-transparent focus:outline-none w-full appearance-none cursor-pointer"
                    style={{ fontFamily: "'Poppins', sans-serif", color: 'rgb(var(--color-text-primary))', fontSize: '15px', lineHeight: '24px' }}
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div
                  className="-translate-y-1/2 absolute content-stretch flex items-center right-[-1px] top-1/2 pointer-events-none"
                  style={{ paddingRight: '10px' }}
                >
                  <div className="overflow-clip relative shrink-0 size-[18px] flex items-center justify-center">
                    <ChevronDownIcon className="w-4 h-4" style={{ color: 'rgb(var(--color-text-secondary))' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Colors multi-select */}
            <MultiSelectDropdown
              label="Colors"
              options={COLOR_OPTIONS}
              selected={form.colors}
              onChange={(v) => updateField('colors', v)}
              floatingLabel
            />

            {/* Sizes multi-select */}
            <MultiSelectDropdown
              label="Sizes"
              options={SIZE_OPTIONS.map((s) => ({ label: s, value: s }))}
              selected={form.sizes}
              onChange={(v) => updateField('sizes', v)}
              floatingLabel
            />
          </div>

          {/* Tags field */}
          <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
            <div
              className="border border-solid content-stretch flex items-center relative shrink-0 w-full min-h-[54px] flex-wrap"
              style={{
                borderColor: 'rgb(var(--color-border-primary) / 0.5)',
                paddingLeft: '14px',
                paddingRight: '14px',
                paddingTop: '8px',
                paddingBottom: '8px',
                borderRadius: '8px',
                gap: '4px',
              }}
            >
              {/* Floating label */}
              {form.tags.length > 0 && (
                <div className="absolute content-stretch flex items-center left-[13px] top-[-9px]" style={{ paddingLeft: '2px', paddingRight: '2px' }}>
                  <div className="-translate-y-1/2 absolute h-[2px] left-0 right-0 top-1/2" style={{ backgroundColor: 'rgb(var(--color-surface-primary))' }} />
                  <div
                    className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 whitespace-nowrap"
                    style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: 'rgb(var(--color-text-secondary))', fontSize: '12px' }}
                  >
                    <p style={{ lineHeight: '18px' }}>Tags</p>
                  </div>
                </div>
              )}
              {/* Chips */}
              <div style={{ display: 'contents' }}>
                {form.tags.map((tag) => (
                  <div
                    key={tag}
                    className="bg-primary-400 content-stretch flex h-[24px] items-center relative shrink-0"
                    style={{
                      gap: '0px',
                      paddingLeft: '3px',
                      paddingRight: '5px',
                      borderRadius: '8px',
                    }}
                  >
                    <div className="content-stretch flex items-start mr-[-2px] relative shrink-0" style={{ paddingLeft: '5px', paddingRight: '5px' }}>
                      <p
                        className="not-italic relative shrink-0 text-center whitespace-nowrap"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 500,
                          lineHeight: '18px',
                          color: 'white',
                          fontSize: '13px',
                        }}
                      >
                        {tag}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                      className="mr-[-2px] opacity-70 hover:opacity-100 overflow-clip relative shrink-0 size-[16px] flex items-center justify-center pointer-events-auto transition-opacity"
                    >
                      <XMarkIcon className="w-3 h-3 text-primary-ink-400" />
                    </button>
                  </div>
                ))}
              </div>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder={form.tags.length === 0 ? 'Tags' : ''}
                maxLength={50}
                className="flex-1 min-w-[80px] leading-6 bg-transparent focus:outline-none"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  color: 'rgb(var(--color-text-primary))',
                  fontSize: '15px',
                }}
              />
            </div>
          </div>

          {/* Gender checkboxes */}
          <div className="content-stretch flex flex-col items-start justify-center relative shrink-0 w-full" style={{ gap: '8px' }}>
            <p
              className="not-italic relative shrink-0 whitespace-nowrap"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                lineHeight: '22px',
                color: 'rgb(var(--color-text-primary))',
                fontSize: '14px',
                letterSpacing: '0px',
              }}
            >
              Gender
            </p>
            <div className="content-stretch flex items-start relative shrink-0" style={{ gap: '16px' }}>
              {(['men', 'women', 'unisex'] as const).map((g) => {
                const checked = form.gender === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => updateField('gender', g)}
                    className="content-stretch flex items-center relative shrink-0"
                  >
                    <div
                      className="content-stretch flex items-center justify-center relative shrink-0"
                      style={{ padding: '8px', borderRadius: '500px' }}
                    >
                      <div className="content-stretch flex items-center justify-center relative shrink-0">
                        <div
                          className="overflow-clip relative shrink-0 size-[20px] flex items-center justify-center rounded border-2 transition-colors"
                          style={{
                            backgroundColor: checked ? 'rgb(var(--color-primary))' : 'transparent',
                            borderColor: checked ? 'rgb(var(--color-primary))' : palette.grey['600'],
                          }}
                        >
                          {checked && (
                            <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
                              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                    <p
                      className="not-italic relative shrink-0 whitespace-nowrap"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        lineHeight: '22px',
                        color: 'rgb(var(--color-text-primary))',
                        fontSize: '14px',
                        letterSpacing: '0px',
                      }}
                    >
                      {g === 'men' ? 'Men' : g === 'women' ? 'Women' : 'Unisex'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </CollapsibleCard>

        {/* ── Points text field (centered, wide) ──────────────────── */}
        <div className="content-stretch flex flex-col h-[54px] items-start min-w-[280px] relative shrink-0 w-full max-w-[720px]">
          <div
            className="border border-solid content-stretch flex items-center relative shrink-0 w-full"
            style={{
              borderColor: errors.price_points ? palette.error['500'] : 'rgba(189,188,201,0.2)',
              paddingLeft: '14px',
              paddingRight: '14px',
              borderRadius: '8px',
            }}
          >
            <div
              className="content-stretch flex flex-[1_0_0] items-center justify-center min-h-px min-w-px relative"
              style={{ paddingTop: '16px', paddingBottom: '16px' }}
            >
              <input
                type="number"
                min={0}
                value={form.price_points}
                onChange={(e) => updateField('price_points', e.target.value)}
                placeholder="Points a learner needs to claim this prize"
                className="flex flex-[1_0_0] flex-col justify-center leading-[0] min-h-px min-w-px not-italic overflow-hidden relative text-ellipsis whitespace-nowrap bg-transparent focus:outline-none w-full"
                style={{ fontFamily: "'Poppins', sans-serif", color: 'rgb(var(--color-text-primary))', fontSize: '15px' }}
              />
            </div>
          </div>
          {errors.price_points && (
            <p className="mt-1 text-xs text-error-500 px-3">{errors.price_points}</p>
          )}
        </div>

        {/* ── Bottom actions row ───────────────────────────────────── */}
        <div className="content-stretch flex items-center justify-end max-w-[720px] relative shrink-0 w-full">
          {/* Publish switch — a real <label>, so the word is part of the
              control instead of floating next to an absolutely-positioned
              knob that could ride over it (Aleix, 2026-09-10). */}
          <label className="flex flex-1 min-w-0 items-center gap-3 px-6 cursor-pointer select-none">
            <button
              type="button"
              role="switch"
              aria-checked={!form.is_draft}
              onClick={() => updateField('is_draft', !form.is_draft)}
              className="relative h-5 w-9 shrink-0 rounded-full transition-colors focus-ring"
              style={{
                backgroundColor: !form.is_draft
                  ? 'rgb(var(--color-primary))'
                  : 'rgba(189,188,201,0.4)',
              }}
            >
              <span
                aria-hidden="true"
                className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  !form.is_draft ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="truncate text-sm text-text-primary">Publish</span>
          </label>

          {/* Create Prize button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            // The form's primary action was painted grey-800: colorless next
            // to the rest of the platform, invisible on a dark surface, and
            // deaf to branding (Aleix, 2026-09-10). As a class, not an inline
            // style, so it takes its readable ink automatically.
            className="bg-primary content-stretch flex items-center justify-center min-h-[48px] min-w-[64px] relative shrink-0 hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{
              gap: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingTop: '8px',
              paddingBottom: '8px',
              borderRadius: '8px',
            }}
          >
            <div
              className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-center whitespace-nowrap"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                color: 'white',
                fontSize: '15px',
              }}
            >
              <p style={{ lineHeight: '26px' }}>
                {isSubmitting ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save changes' : 'Create Prize'}
              </p>
            </div>
          </button>
        </div>

      </div>
    </motion.div>
  );
}
